import { logger } from '@inboundx/shared/platform';

import type { Notice, NoticeDraft } from '@/domain/notices';
import { supabase } from '@/lib/supabase';

/** The table lives in the `backoffice` schema, not `public`. */
const notices = () => supabase.schema('backoffice').from('notices');

const COLUMNS = 'id, headline, detail, level, area, link, is_active, displayed_at, created_at';

/**
 * What a request carrying no usable access token comes back as. Only
 * `authenticated` has USAGE on the `backoffice` schema, so an expired token — or
 * a focus refetch racing the token refresh — reaches PostgREST as `anon` and
 * Postgres refuses the schema (42501); PGRST301 is the JWT's own rejection.
 * Reads in `public` never show this: `anon` has USAGE there and RLS just returns
 * no rows.
 */
const NO_SESSION_CODES = new Set(['42501', 'PGRST301']);

/** The notice currently shown in the banner, or null when none is active. */
export async function fetchActiveNotice(): Promise<Notice | null> {
    const { data, error } = await notices()
        .select(COLUMNS)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error) {
        // The banner is optional chrome polled every couple of minutes, so an
        // auth transient degrades to "no notice" rather than paging us.
        if (NO_SESSION_CODES.has(error.code)) return null;
        logger.error('Error fetching active notice:', error);
        throw error;
    }
    return (data as Notice | null) ?? null;
}

/** Every notice ever created, newest first (staff management page). */
export async function fetchNotices(): Promise<Notice[]> {
    const { data, error } = await notices()
        .select(COLUMNS)
        .order('created_at', { ascending: false });

    if (error) {
        logger.error('Error fetching notices:', error);
        throw error;
    }
    return (data ?? []) as Notice[];
}

/** Insert a draft, returning its id so the caller can display it right away. */
export async function createNotice(draft: NoticeDraft): Promise<string> {
    const { data, error } = await notices().insert(draft).select('id').single();
    if (error) {
        logger.error('Error creating notice:', error);
        throw error;
    }
    return (data as { id: string }).id;
}

/** Edit a notice, optionally making an active row a draft in the same update. */
export async function updateNotice(
    id: string,
    draft: NoticeDraft,
    options: { deactivate: boolean }
): Promise<void> {
    const changes = options.deactivate ? { ...draft, is_active: false } : draft;
    const { error } = await notices().update(changes).eq('id', id);
    if (error) {
        logger.error('Error updating notice:', error);
        throw error;
    }
}

/** Make `id` the displayed notice, deactivating whichever was active before. */
export async function activateNotice(id: string): Promise<void> {
    await deactivateAllNotices();
    // displayed_at is what a `resolved` notice's 24h expiry counts from, and
    // what the banner shows as the incident time.
    const { error } = await notices()
        .update({ is_active: true, displayed_at: new Date().toISOString() })
        .eq('id', id);
    if (error) {
        logger.error('Error activating notice:', error);
        throw error;
    }
}

/** Hide the banner for everyone without deleting anything. */
export async function deactivateAllNotices(): Promise<void> {
    const { error } = await notices().update({ is_active: false }).eq('is_active', true);
    if (error) {
        logger.error('Error deactivating notices:', error);
        throw error;
    }
}

export async function deleteNotice(id: string): Promise<void> {
    const { error } = await notices().delete().eq('id', id);
    if (error) {
        logger.error('Error deleting notice:', error);
        throw error;
    }
}
